import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import {
  X,
  Search,
  Check,
  ChevronLeft,
  ChevronRight,
  Calendar,
  Users,
  Play,
  Pause,
  Volume2,
  XCircle,
  Upload,
  Music,
  AlertCircle,
  Loader2,
} from "lucide-react";
import { ROSTER_API, fetchWithAuth, uploadToS3 } from "../../utils/api";
import { SkeletonRoutineFormModal } from "../ui/SkeletonLoader";

// Constants for pagination
const DANCERS_PER_PAGE = 8;

const RoutineFormModal = ({
  isOpen,
  onClose,
  routine,
  onSubmit, // (routineData) => Promise<routineResponse>
  onComplete, // New callback for when upload is complete
  isEditMode = false,
  isDarkMode = false,
  roster = [], // Accept roster data as prop to prevent duplicate API calls
}) => {
  const audioRef = useRef(null);

  // Enhanced close handler that resets form state
  const handleClose = () => {
    // Reset all form state immediately when closing
    setRoutineTitle("");
    setSelectedDancers([]);
    setAudioSrc(null);
    setSongFileName(null);
    setSelectedFile(null);
    setFileError(null);
    setUploadProgress(0);
    setUploadState(null);
    setDancerSearchQuery("");
    
    // Call the original onClose
    onClose();
  };

  // Inline normalizeRoles
  const normalizeRoles = (roleValue) => {
    if (!roleValue) return [];
    if (Array.isArray(roleValue) && typeof roleValue[0] === "string") {
      return roleValue;
    }
    if (
      Array.isArray(roleValue) &&
      typeof roleValue[0] === "object" &&
      "S" in roleValue[0]
    ) {
      return roleValue.map((r) => r.S);
    }
    if (typeof roleValue === "string") {
      return [roleValue];
    }
    return [];
  };

  // Loading states (no need to fetch roster - comes from props)
  const [isFetching, setIsFetching] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadState, setUploadState] = useState(null); // null, "uploading", "success", "error"

  // Form states
  const [routineTitle, setRoutineTitle] = useState(routine?.title || "");
  const [selectedDancers, setSelectedDancers] = useState(
    routine?.dancers || []
  );
  const [songFileName, setSongFileName] = useState(
    routine?.songFileName || null
  );

  // Audio states
  const [fileError, setFileError] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [audioSrc, setAudioSrc] = useState(null);

  // Playback state
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);

  // Roster / search / pagination
  const [dancerSearchQuery, setDancerSearchQuery] = useState("");
  const [averageAge, setAverageAge] = useState(0);
  const [ageDistribution, setAgeDistribution] = useState({
    min: 0,
    max: 0,
    median: 0,
  });

  // 🚀 PERFORMANCE: Scrolling performance states  
  const [isScrolling, setIsScrolling] = useState(false);
  const scrollTimeoutRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const containerRef = useRef(null);
  
  // 👁️ VISUAL: Scroll indicator states
  const selectedContainerRef = useRef(null);
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);
  
  // Debounced search to prevent excessive filtering during typing
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");

  // Reset modal state when opened/closed or routine changes
  useEffect(() => {
    if (isOpen) {
      // Modal is opening - initialize with routine data
      setRoutineTitle(routine?.title || "");
      setSelectedDancers(routine?.dancers || []);
      if (routine?.songFileUrl) {
        setAudioSrc(routine.songFileUrl);
        setSongFileName(routine.songFileName);
      } else {
        setAudioSrc(null);
        setSongFileName(null);
      }
      setSelectedFile(null);
      setFileError(null);
      setUploadProgress(0);
      setUploadState(null);
    } else {
      // Modal is closing - reset all form state to prevent stale data
      setRoutineTitle("");
      setSelectedDancers([]);
      setAudioSrc(null);
      setSongFileName(null);
      setSelectedFile(null);
      setFileError(null);
      setUploadProgress(0);
      setUploadState(null);
      setDancerSearchQuery(""); // Also reset search
    }
  }, [isOpen, routine]);

  // Process roster when modal opens or roster prop changes
  useEffect(() => {
    if (!isOpen || !roster) return;
    
    setIsFetching(false); // Roster is already available from props
    console.log(`📊 Using ${roster.length} roster members from props (no API call needed)`);
  }, [isOpen, roster]);

  // Calculate age statistics
  useEffect(() => {
    if (selectedDancers.length === 0) {
      setAverageAge(0);
      setAgeDistribution({ min: 0, max: 0, median: 0 });
      return;
    }

    const selectedMembers = roster.filter((m) =>
      selectedDancers.includes(m.dancerId)
    );
    const ages = selectedMembers
      .map((m) => {
        if (!m.dateOfBirth) return 0;
        const birthYear = new Date(m.dateOfBirth).getFullYear();
        return new Date().getFullYear() - birthYear;
      })
      .filter((age) => age > 0);

    if (ages.length === 0) return;

    const sortedAges = [...ages].sort((a, b) => a - b);
    const min = sortedAges[0];
    const max = sortedAges[sortedAges.length - 1];
    const median = sortedAges[Math.floor(sortedAges.length / 2)];
    const average = Math.round(
      ages.reduce((sum, age) => sum + age, 0) / ages.length
    );

    setAverageAge(average);
    setAgeDistribution({ min, max, median });
  }, [selectedDancers, roster]);

  // 🚀 PERFORMANCE: Memoized filtering and sorting to prevent unnecessary recalculations
  const filteredRoster = useMemo(() => {
    return roster
      .filter((member) => {
        const roles = normalizeRoles(member.role);
        if (!roles.includes("Dancer")) return false;
        
        // Client-side search filtering with debounced query
        if (debouncedSearchQuery.trim()) {
          const fullName = `${member.firstName} ${member.lastName}`.toLowerCase();
          const searchTerm = debouncedSearchQuery.toLowerCase().trim();
          return fullName.includes(searchTerm);
        }
        
        return true; // Show all dancers if no search query
      })
      .sort((a, b) => {
        // Sort existing dancers to the top
        const aIsSelected = selectedDancers.includes(a.dancerId);
        const bIsSelected = selectedDancers.includes(b.dancerId);
        
        if (aIsSelected && !bIsSelected) return -1;
        if (!aIsSelected && bIsSelected) return 1;
        
        // If both selected or both not selected, sort alphabetically
        const aName = `${a.firstName} ${a.lastName}`.toLowerCase();
        const bName = `${b.firstName} ${b.lastName}`.toLowerCase();
        return aName.localeCompare(bName);
      });
  }, [roster, debouncedSearchQuery, selectedDancers]);

  // 🚀 PERFORMANCE: Handle scrolling performance by disabling transitions during scroll
  const handleScroll = useCallback((e) => {
    // Set scrolling state to disable transitions
    setIsScrolling(true);
    
    // Clear existing timeout
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
    
    // Re-enable transitions after scrolling stops
    scrollTimeoutRef.current = setTimeout(() => {
      setIsScrolling(false);
    }, 150);
  }, []);

  // 🚀 PERFORMANCE: Debounce search input to prevent excessive filtering
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchQuery(dancerSearchQuery);
    }, 200); // 200ms debounce
    
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [dancerSearchQuery]);

  // 👁️ VISUAL: Check if selected dancers container needs scroll indicator
  useEffect(() => {
    const checkScrollIndicator = () => {
      if (selectedContainerRef.current && selectedDancers.length > 0) {
        const { scrollHeight, clientHeight } = selectedContainerRef.current;
        setShowScrollIndicator(scrollHeight > clientHeight);
      } else {
        setShowScrollIndicator(false);
      }
    };

    // Check initially and whenever selected dancers change
    checkScrollIndicator();
    
    // Also check after a short delay to ensure DOM has updated
    const timeoutId = setTimeout(checkScrollIndicator, 100);
    
    return () => clearTimeout(timeoutId);
  }, [selectedDancers, isOpen]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // File change => local blob preview
  const handleFileChange = async (e) => {
    setFileError(null);
    setUploadState(null);
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("audio/")) {
      setFileError("Please select an audio file");
      setSelectedFile(null);
      return;
    }

    // Create local preview
    const localPreviewUrl = URL.createObjectURL(file);
    setSelectedFile(file);
    setSongFileName(file.name);
    setAudioSrc(localPreviewUrl);

    // Reset audio player state
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  };

  // Upload file with progress tracking
  const uploadFileWithProgress = async (url, file) => {
    setUploadState("uploading");
    setUploadProgress(0);

    try {
      // Use the enhanced uploadToS3 function that tracks progress
      await uploadToS3(url, file, (progress) => {
        setUploadProgress(progress);
      });

      // Set upload as successful
      setUploadState("success");
      return true;
    } catch (error) {
      console.error("Error uploading to S3:", error);
      setUploadState("error");
      setFileError("Failed to upload audio file. Please try again.");
      throw error;
    }
  };

  // Modified handleSubmit function to work with the prominent progress overlay
const handleSubmit = async () => {
  try {
    setIsSubmitting(true);
    setFileError(null);

    if (!routineTitle) {
      setFileError("Please enter a routine title");
      setIsSubmitting(false);
      return;
    }

    if (selectedDancers.length === 0) {
      setFileError("Please select at least one dancer");
      setIsSubmitting(false);
      return;
    }

    const routineData = {
      title: routineTitle,
      dancers: selectedDancers,
      averageAge,
    };

    // If we have a new file selected, add file info for presigned URL
    if (selectedFile) {
      routineData.fileName = selectedFile.name;
      routineData.fileType = selectedFile.type;
    }

    // Submit to backend - this creates the DynamoDB entry
    let updatedRoutine;
    try {
      updatedRoutine = await onSubmit(routineData);
    } catch (submitError) {
      setFileError("Failed to create routine");
      setIsSubmitting(false);
      return; // Exit early on database save failure
    }

    // If we got back an upload URL and we have a file, handle S3 upload
    if (updatedRoutine?.uploadUrl && selectedFile) {
      try {
        // Set upload state to show progress overlay
        setUploadState("uploading");
        setUploadProgress(0);
        
        // Upload the file with progress tracking
        await uploadToS3(updatedRoutine.uploadUrl, selectedFile, (progress) => {
          setUploadProgress(progress);
        });
        
        // Upload succeeded, show success state
        setUploadState("success");
        
        // Add a delay to ensure S3 consistency before closing the modal
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Show success state briefly before closing
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Call onComplete with the updated routine
        if (onComplete) {
          onComplete(updatedRoutine);
        }
        
        // Close the modal
        handleClose();
        
      } catch (uploadError) {
        // Upload failed but the database entry was created
        setFileError(
          "Failed to upload music file. The routine was saved in the database, but you'll need to re-upload the music file."
        );
        setUploadState("error");
        setIsSubmitting(false);
      }
    } else {
      // No file upload needed
      setUploadState("success");
      
      // Show success state briefly
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Call onComplete with the updated routine
      if (onComplete) {
        onComplete(updatedRoutine);
      }
      
      // Close the modal
      handleClose();
    }
    
    setIsSubmitting(false);
    
  } catch (error) {
    // Catch any other errors
    setFileError("An unexpected error occurred");
    setUploadState("error");
    setIsSubmitting(false);
  }
};

  // Cleanup local blob URLs when component unmounts or audio source changes
  useEffect(() => {
    return () => {
      if (audioSrc?.startsWith("blob:")) {
        URL.revokeObjectURL(audioSrc);
      }
      // Reset audio player state
      setIsPlaying(false);
      setCurrentTime(0);
      setDuration(0);
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
    };
  }, [audioSrc]);

  // Reset modal state when it opens/closes
  useEffect(() => {
    if (!isOpen) {
      setIsPlaying(false);
      setCurrentTime(0);
      setDuration(0);
      setFileError(null);
      setUploadProgress(0);
      setUploadState(null);
      setIsSubmitting(false);
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
    } else {
      setFileError(null);
      setUploadProgress(0);
      setUploadState(null);
      setIsSubmitting(false);
    }
  }, [isOpen]);

  // Toggle playback
  const togglePlayback = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play();
      setIsPlaying(true);
    }
  };

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  const getInitials = (firstName, lastName) =>
    `${firstName?.[0] || ""}${lastName?.[0] || ""}`.toUpperCase();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm"
        onClick={isSubmitting ? null : handleClose}
      />
      <div className="relative min-h-screen flex items-center justify-center p-4">
        <div
          className={`relative w-full max-w-5xl ${
            isDarkMode
              ? "bg-[#1a1f2d]/95 backdrop-blur-xl border border-white/5 shadow-2xl"
              : "bg-white shadow-xl"
          } rounded-2xl animate-scale-in overflow-hidden`}
        >
          {/* Header */}
          <div
            className={`flex items-center justify-between px-8 py-6 ${
              isDarkMode
                ? "border-b border-white/5"
                : "border-b border-gray-100"
            }`}
          >
            <div className="flex items-center gap-6">
              <h3
                className={`text-xl font-semibold ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                {isEditMode ? "Edit Routine" : "Create New Routine"}
              </h3>
              {selectedDancers.length > 0 && (
                <div
                  className={`flex items-center gap-6 px-4 py-1.5 rounded-full text-sm ${
                    isDarkMode ? "bg-white/5" : "bg-gray-50"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 opacity-70" />
                    <span>{selectedDancers.length} dancers</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 opacity-70" />
                    <span>
                      {(() => {
                        // 🧠 SMART: Show single age vs range based on actual age differences
                        if (selectedDancers.length === 1) {
                          // Single dancer - show just the age
                          return `${ageDistribution.min} years`;
                        } else if (ageDistribution.min === ageDistribution.max) {
                          // Multiple dancers, same age - show single age
                          return `${ageDistribution.min} years`;
                        } else {
                          // Multiple dancers, different ages - show range
                          return `Ages ${ageDistribution.min}-${ageDistribution.max}`;
                        }
                      })()}
                    </span>
                  </div>
                </div>
              )}
            </div>
            {!isSubmitting && (
              <button
                onClick={handleClose}
                className={`rounded-full p-2 transition-colors ${
                  isDarkMode
                    ? "hover:bg-white/10 text-gray-400"
                    : "hover:bg-gray-100 text-gray-500"
                }`}
              >
                <X className="h-5 w-5" />
              </button>
            )}
          </div>

          {/* Content Area */}
          <div className="p-8 space-y-8">
            {isFetching ? (
              <SkeletonRoutineFormModal />
            ) : (
              <div className="grid grid-cols-3 gap-8">
                {/* Left Column - Main Info */}
                <div className="col-span-2 space-y-6">
                  {/* Title Input */}
                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${
                        isDarkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Routine Title
                    </label>
                    <input
                      type="text"
                      value={routineTitle}
                      onChange={(e) => setRoutineTitle(e.target.value)}
                      className={`w-full px-4 py-2.5 rounded-xl text-base ${
                        isDarkMode
                          ? "bg-[#1a1f2d] border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent"
                          : "bg-gray-50 border border-gray-200 text-gray-900 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500/50"
                      } transition-all duration-200`}
                      placeholder="Enter a title for your routine"
                      disabled={isSubmitting}
                    />
                  </div>

                  {/* Dancers Table */}
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <label
                          className={`text-sm font-medium ${
                            isDarkMode ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          Select Dancers
                        </label>
                        {selectedDancers.length > 0 && (
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-medium ${
                              isDarkMode
                                ? "bg-blue-500/20 text-blue-300"
                                : "bg-blue-100 text-blue-700"
                            }`}
                          >
                            {selectedDancers.length} selected
                          </span>
                        )}
                      </div>
                      <div className="relative w-64">
                        <input
                          type="text"
                          value={dancerSearchQuery}
                          onChange={(e) => setDancerSearchQuery(e.target.value)}
                          className={`w-full pr-9 pl-4 py-2 rounded-lg text-sm ${
                            isDarkMode
                              ? "bg-[#1a1f2d] border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                              : "bg-gray-50 border border-gray-200 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500/50"
                          }`}
                          placeholder="Search dancers..."
                          disabled={isSubmitting}
                        />
                        <Search
                          className={`absolute right-3 top-2.5 h-4 w-4 ${
                            isDarkMode ? "text-gray-500" : "text-gray-400"
                          }`}
                        />
                      </div>
                    </div>

                                          <div
                        className={`rounded-xl border overflow-hidden ${
                          isDarkMode ? "border-white/10" : "border-gray-200"
                        }`}
                      >
                        <div
                          className={`overflow-y-auto max-h-[400px] ${
                            isDarkMode
                              ? "custom-scrollbar-dark"
                              : "custom-scrollbar"
                          }`}
                          onScroll={handleScroll}
                          ref={containerRef}
                        >
                        <table className="w-full">
                          <thead
                            className={`text-sm ${
                              isDarkMode ? "bg-[#252D42]" : "bg-gray-50"
                            }`}
                          >
                            <tr>
                              <th className="w-12 px-4 py-3 text-left"></th>
                              <th className="px-4 py-3 text-left font-medium">
                                Name
                              </th>
                              <th className="px-4 py-3 text-left font-medium">
                                Age
                              </th>
                              <th className="w-12 px-4 py-3 text-left"></th>
                            </tr>
                          </thead>
                          <tbody
                            className={`divide-y ${
                              isDarkMode ? "divide-white/5" : "divide-gray-100"
                            }`}
                          >
                            {filteredRoster.map((member) => {
                              const isSelected = selectedDancers.includes(
                                member.dancerId
                              );
                              const age = member.dateOfBirth
                                ? new Date().getFullYear() -
                                  new Date(member.dateOfBirth).getFullYear()
                                : null;

                              return (
                                <tr
                                  key={member.dancerId}
                                  onClick={() => {
                                    if (isSubmitting) return;
                                    setSelectedDancers((prev) =>
                                      isSelected
                                        ? prev.filter(
                                            (id) => id !== member.dancerId
                                          )
                                        : [...prev, member.dancerId]
                                    );
                                  }}
                                  className={`group cursor-pointer ${
                                    // 🚀 PERFORMANCE: Disable transitions during scrolling to prevent lag
                                    isScrolling ? "" : "transition-all duration-200"
                                  } ${
                                    isSelected
                                      ? isDarkMode
                                        ? "bg-blue-500/20 border-l-4 border-blue-400 shadow-sm"
                                        : "bg-blue-50 border-l-4 border-blue-500 shadow-sm"
                                      : isDarkMode
                                      ? "hover:bg-white/5 border-l-4 border-transparent"
                                      : "hover:bg-gray-50 border-l-4 border-transparent"
                                  } ${
                                    isSubmitting
                                      ? "opacity-70 pointer-events-none"
                                      : ""
                                  }`}
                                >
                                  <td className="px-4 py-2.5">
                                    {member.profilePicture ? (
                                      <img
                                        src={member.profilePicture}
                                        alt=""
                                        className="w-8 h-8 rounded-full object-cover"
                                      />
                                    ) : (
                                      <div
                                        className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                                          isDarkMode
                                            ? "bg-[#1a1f2d]"
                                            : "bg-gray-100"
                                        }`}
                                      >
                                        {member.firstName[0]}
                                        {member.lastName[0]}
                                      </div>
                                    )}
                                  </td>
                                  <td
                                    className={`px-4 py-2.5 ${
                                      isDarkMode
                                        ? "text-white"
                                        : "text-gray-900"
                                    }`}
                                  >
                                    <div className="flex items-center gap-2">
                                      <span>{member.firstName} {member.lastName}</span>
                                      {isSelected && (
                                        <span
                                          className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                            isDarkMode
                                              ? "bg-green-500/20 text-green-300"
                                              : "bg-green-100 text-green-700"
                                          }`}
                                        >
                                          In routine
                                        </span>
                                      )}
                                    </div>
                                  </td>
                                  <td
                                    className={`px-4 py-2.5 text-sm ${
                                      isDarkMode
                                        ? "text-gray-400"
                                        : "text-gray-500"
                                    }`}
                                  >
                                    {age ? `${age} years` : "-"}
                                  </td>
                                  <td className="px-4 py-2.5">
                                    <div
                                      className={`${
                                        // 🚀 PERFORMANCE: Disable opacity transitions during scrolling
                                        isScrolling ? "" : "transition-opacity"
                                      } ${
                                        isSelected
                                          ? "opacity-100"
                                          : "opacity-0 group-hover:opacity-100"
                                      }`}
                                    >
                                      <Check
                                        className={`h-5 w-5 ${
                                          isDarkMode
                                            ? "text-blue-400"
                                            : "text-blue-600"
                                        }`}
                                      />
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Show count of filtered dancers */}
                    {!isFetching && (
                      <div className={`mt-3 text-center text-sm ${
                        isDarkMode ? "text-gray-400" : "text-gray-500"
                      }`}>
                        Showing {filteredRoster.length} of {roster.filter(m => normalizeRoles(m.role).includes("Dancer")).length} dancers
                        {debouncedSearchQuery && ` matching "${debouncedSearchQuery}"`}
                      </div>
                    )}
                  </div>

                {/* Audio Section */}
<div>
  <label
    className={`block text-sm font-medium mb-2 ${
      isDarkMode ? "text-gray-300" : "text-gray-700"
    }`}
  >
    Music Track
  </label>
  <div
    className={`rounded-xl overflow-hidden ${
      isDarkMode
        ? "bg-[#1a1f2d] border border-white/10"
        : "bg-gray-50 border border-gray-200"
    }`}
  >
    {audioSrc ? (
      <div className="p-4">
        <div className="flex items-center gap-4">
          <div className="flex-1 flex items-center gap-4">
            <button
              onClick={togglePlayback}
              disabled={isSubmitting}
              className={`w-10 h-10 flex items-center justify-center rounded-full transition-colors ${
                isDarkMode
                  ? "bg-white/5 hover:bg-white/10"
                  : "bg-white hover:bg-gray-100 border border-gray-200"
              } ${
                isSubmitting
                  ? "opacity-50 cursor-not-allowed"
                  : ""
              }`}
            >
              {isPlaying ? (
                <Pause className="w-5 h-5" />
              ) : (
                <Play className="w-5 h-5 ml-0.5" />
              )}
            </button>
            <div className="flex-1">
              <div className="relative w-full h-1.5 rounded-full overflow-hidden bg-gray-200 dark:bg-white/10">
                <div
                  className="absolute inset-y-0 left-0 bg-blue-500 dark:bg-blue-400 transition-all duration-100"
                  style={{
                    width: `${
                      (currentTime / duration) * 100
                    }%`,
                  }}
                />
                <input
                  type="range"
                  min="0"
                  max={duration || 100}
                  value={currentTime}
                  onChange={(e) => {
                    if (isSubmitting) return;
                    const time = parseFloat(e.target.value);
                    setCurrentTime(time);
                    if (audioRef.current) {
                      audioRef.current.currentTime = time;
                    }
                  }}
                  className={`absolute inset-0 w-full h-full opacity-0 cursor-pointer ${
                    isSubmitting ? "pointer-events-none" : ""
                  }`}
                  disabled={isSubmitting}
                />
              </div>
              <div className="flex justify-between text-xs mt-1.5">
                <span
                  className={
                    isDarkMode
                      ? "text-gray-400"
                      : "text-gray-500"
                  }
                >
                  {formatTime(currentTime)}
                </span>
                <span
                  className={
                    isDarkMode
                      ? "text-gray-400"
                      : "text-gray-500"
                  }
                >
                  {formatTime(duration)}
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <label
              className={`cursor-pointer px-3 py-1.5 rounded-lg text-sm transition-colors ${
                isDarkMode
                  ? "bg-white/5 hover:bg-white/10 text-gray-300"
                  : "bg-white hover:bg-gray-100 text-gray-600 border border-gray-200"
              } ${
                isSubmitting
                  ? "opacity-50 pointer-events-none"
                  : ""
              }`}
            >
              <input
                type="file"
                accept="audio/*"
                onChange={handleFileChange}
                className="hidden"
                disabled={isSubmitting}
              />
              Replace
            </label>
            <button
              onClick={() => {
                if (isSubmitting) return;
                setAudioSrc(null);
                setSelectedFile(null);
                setCurrentTime(0);
                setDuration(0);
                setIsPlaying(false);
                setUploadState(null);
                setUploadProgress(0);
              }}
              disabled={isSubmitting}
              className={`p-1.5 rounded-lg transition-colors ${
                isDarkMode
                  ? "hover:bg-white/10 text-gray-400"
                  : "hover:bg-gray-100 text-gray-500"
              } ${
                isSubmitting
                  ? "opacity-50 cursor-not-allowed"
                  : ""
              }`}
            >
              <XCircle className="w-5 h-5" />
            </button>
          </div>
        </div>
        <audio
          ref={audioRef}
          src={audioSrc}
          className="hidden"
          onError={(e) => {
            console.error("Audio loading error:", e);
            setFileError("Error loading audio file");
          }}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          onLoadedMetadata={(e) => {
            setDuration(e.target.duration || 0);
            setCurrentTime(0);
            e.target.currentTime = 0;
          }}
          onTimeUpdate={(e) =>
            setCurrentTime(e.target.currentTime || 0)
          }
          onEnded={() => {
            setIsPlaying(false);
            setCurrentTime(0);
            if (audioRef.current) {
              audioRef.current.currentTime = 0;
            }
          }}
        />
      </div>
    ) : (
      <label className="flex flex-col items-center justify-center py-8 cursor-pointer transition-colors hover:bg-black/5">
        <input
          type="file"
          accept="audio/*"
          onChange={handleFileChange}
          className="hidden"
          disabled={isSubmitting}
        />
        <div
          className={`mb-2 p-3 rounded-full ${
            isDarkMode ? "bg-white/5" : "bg-white"
          }`}
        >
          <svg
            className="w-6 h-6 opacity-70"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3"
            />
          </svg>
        </div>
        <div
          className={`text-sm font-medium ${
            isDarkMode ? "text-gray-400" : "text-gray-600"
          }`}
        >
          Click to upload music
        </div>
        <div
          className={`text-xs mt-1 ${
            isDarkMode ? "text-gray-500" : "text-gray-400"
          }`}
        >
          MP3, WAV up to 10MB
        </div>
      </label>
    )}
  </div>
  {fileError && (
    <p className="text-red-500 text-sm mt-2">{fileError}</p>
  )}
</div>
</div>
                {/* Right Column - Selected Dancers */}
                <div className="col-span-1">
                  <div
                    className={`rounded-xl border h-full ${
                      isDarkMode ? "border-white/10" : "border-gray-200"
                    }`}
                  >
                    <div
                      className={`p-4 border-b ${
                        isDarkMode ? "border-white/10" : "border-gray-100"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <h4
                          className={`font-medium ${
                            isDarkMode ? "text-white" : "text-gray-900"
                          }`}
                        >
                          Selected Dancers
                        </h4>
                        {/* 🔢 FEATURE: Dancer count tally */}
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${
                            selectedDancers.length > 0
                              ? isDarkMode
                                ? "bg-blue-500/20 text-blue-300"
                                : "bg-blue-100 text-blue-700"
                              : isDarkMode
                              ? "bg-gray-700 text-gray-400"
                              : "bg-gray-100 text-gray-500"
                          }`}
                        >
                          {selectedDancers.length}
                        </span>
                      </div>
                      {selectedDancers.length > 0 && (
                        <p
                          className={`text-sm mt-1 ${
                            isDarkMode ? "text-gray-400" : "text-gray-500"
                          }`}
                        >
                          Average age: {averageAge} years
                        </p>
                      )}
                    </div>
                    {/* 📏 FEATURE: Fixed height with scrolling instead of expanding */}
                    <div className="relative">
                      <div
                        ref={selectedContainerRef}
                        className={`${
                          selectedDancers.length === 0
                            ? "flex items-center justify-center h-[400px] p-4"
                            : "h-[400px] overflow-y-auto p-4"
                        } ${
                          isDarkMode
                            ? "custom-scrollbar-dark"
                            : "custom-scrollbar"
                        }`}
                      >
                      {selectedDancers.length === 0 ? (
                        <div className="text-center">
                          <div
                            className={`p-3 rounded-full inline-flex mb-2 ${
                              isDarkMode ? "bg-white/5" : "bg-gray-100"
                            }`}
                          >
                            <Users className="w-6 h-6 opacity-70" />
                          </div>
                          <p
                            className={`text-sm ${
                              isDarkMode ? "text-gray-400" : "text-gray-500"
                            }`}
                          >
                            No dancers selected yet
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {roster
                            .filter((m) => selectedDancers.includes(m.dancerId))
                            .map((member) => {
                              const age = member.dateOfBirth
                                ? new Date().getFullYear() -
                                  new Date(member.dateOfBirth).getFullYear()
                                : null;

                              return (
                                <div
                                  key={member.dancerId}
                                  className={`group flex items-center justify-between p-2 rounded-lg ${
                                    isDarkMode
                                      ? "hover:bg-white/5"
                                      : "hover:bg-gray-50"
                                  }`}
                                >
                                  <div className="flex items-center gap-3">
                                    {member.profilePicture ? (
                                      <img
                                        src={member.profilePicture}
                                        alt=""
                                        className="w-8 h-8 rounded-full object-cover"
                                      />
                                    ) : (
                                      <div
                                        className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                                          isDarkMode
                                            ? "bg-[#1a1f2d]"
                                            : "bg-gray-100"
                                        }`}
                                      >
                                        {member.firstName[0]}
                                        {member.lastName[0]}
                                      </div>
                                    )}
                                    <div>
                                      <div
                                        className={
                                          isDarkMode
                                            ? "text-white"
                                            : "text-gray-900"
                                        }
                                      >
                                        {member.firstName} {member.lastName}
                                      </div>
                                      {age && (
                                        <div
                                          className={`text-xs ${
                                            isDarkMode
                                              ? "text-gray-400"
                                              : "text-gray-500"
                                          }`}
                                        >
                                          {age} years old
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  <button
                                    onClick={(e) => {
                                      if (isSubmitting) return;
                                      e.stopPropagation();
                                      setSelectedDancers((prev) =>
                                        prev.filter(
                                          (id) => id !== member.dancerId
                                        )
                                      );
                                    }}
                                    disabled={isSubmitting}
                                    className={`p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity ${
                                      isDarkMode
                                        ? "hover:bg-white/10 text-gray-400"
                                        : "hover:bg-gray-200 text-gray-500"
                                    } ${
                                      isSubmitting ? "cursor-not-allowed" : ""
                                    }`}
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                </div>
                              );
                            })}
                        </div>
                      )}
                      </div>
                      
                      {/* 👁️ VISUAL: Scroll indicators when content overflows */}
                      {showScrollIndicator && selectedDancers.length > 0 && (
                        <>
                          {/* Bottom fade gradient */}
                          <div
                            className={`absolute bottom-0 left-0 right-0 h-12 pointer-events-none z-10 ${
                              isDarkMode
                                ? "bg-gradient-to-t from-[#1f2937] to-transparent"
                                : "bg-gradient-to-t from-white to-transparent"
                            }`}
                          />
                          
                          {/* Scroll hint text */}
                          <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 z-20">
                            <div
                              className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${
                                isDarkMode
                                  ? "bg-gray-700/90 text-gray-300 border border-gray-600"
                                  : "bg-gray-100/90 text-gray-600 border border-gray-200"
                              }`}
                            >
                              <span>Scroll for more</span>
                              <svg 
                                className="w-3 h-3" 
                                fill="none" 
                                stroke="currentColor" 
                                viewBox="0 0 24 24"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                              </svg>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div
            className={`flex items-center justify-end px-8 py-6 gap-3 ${
              isDarkMode
                ? "border-t border-white/5"
                : "border-t border-gray-100"
            }`}
          >
            <button
              onClick={handleClose}
              disabled={isSubmitting}
              className={`px-4 py-2 rounded-lg text-sm transition-colors ${
                isDarkMode
                  ? "bg-white/5 hover:bg-white/10 text-white"
                  : "bg-gray-100 hover:bg-gray-200 text-gray-700"
              } ${isSubmitting ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              Cancel
            </button>
            {!isFetching && (
              <button
                onClick={handleSubmit}
                disabled={
                  !routineTitle ||
                  selectedDancers.length === 0 ||
                  isSubmitting ||
                  uploadState === "uploading"
                }
                className={`px-6 py-2 rounded-lg text-sm font-medium transition-all ${
                  isDarkMode
                    ? "bg-blue-500 text-white hover:bg-blue-600 disabled:opacity-50 disabled:hover:bg-blue-500"
                    : "bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:hover:bg-blue-600"
                } ${isSubmitting ? "relative pl-9" : ""}`}
              >
                {isSubmitting && (
                  <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                  </div>
                )}
                {isSubmitting
                  ? uploadState === "uploading"
                    ? "Uploading..."
                    : "Saving..."
                  : isEditMode
                  ? "Save Changes"
                  : "Create Routine"}
              </button>
            )}
            
          </div>
        </div>
      </div>

      {/* Prominent Upload Progress Overlay */}
      {(isSubmitting || uploadState === "uploading" || uploadState === "success") && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className={`relative w-full max-w-md p-6 rounded-xl shadow-xl ${
            isDarkMode 
              ? "bg-[#1a1f2d]/95 border border-white/10" 
              : "bg-white border border-gray-100"
          }`}>
            <div className="space-y-6">
              {/* Icon & Title based on state */}
              <div className="flex flex-col items-center text-center">
                {uploadState === "uploading" ? (
                  <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
                    isDarkMode ? "bg-blue-500/20" : "bg-blue-100"
                  }`}>
                    <div className={`animate-spin rounded-full h-10 w-10 border-4 ${
                      isDarkMode
                        ? "border-blue-400 border-t-transparent"
                        : "border-blue-600 border-t-transparent"
                    }`} />
                  </div>
                ) : uploadState === "success" ? (
                  <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
                    isDarkMode ? "bg-green-500/20" : "bg-green-100"
                  }`}>
                    <svg className={`w-8 h-8 ${isDarkMode ? "text-green-400" : "text-green-600"}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                ) : (
                  <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
                    isDarkMode ? "bg-blue-500/20" : "bg-blue-100"
                  }`}>
                    <svg className={`w-8 h-8 ${isDarkMode ? "text-blue-400" : "text-blue-600"}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8 8-4-4" />
                    </svg>
                  </div>
                )}
                
                <h2 className={`mt-4 text-xl font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}>
                  {uploadState === "uploading" 
                    ? "Creating Routine..." 
                    : uploadState === "success" 
                      ? "Routine Created Successfully!"
                      : "Saving Routine..."}
                </h2>
                
                <p className={`mt-2 ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}>
                  {uploadState === "uploading" 
                    ? "Please wait while we save your routine and upload your music."
                    : uploadState === "success"
                      ? "Your routine has been saved and is ready to use."
                      : "Creating routine in the database..."}
                </p>
              </div>
              
              {/* Progress Bar (only for uploading state) */}
              {uploadState === "uploading" && (
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className={isDarkMode ? "text-gray-300" : "text-gray-600"}>
                      Progress
                    </span>
                    <span className={`font-medium ${
                      uploadProgress >= 95 
                        ? isDarkMode ? "text-green-400" : "text-green-600"
                        : isDarkMode ? "text-blue-400" : "text-blue-600"
                    }`}>
                      {uploadProgress >= 100 
                        ? "Finalizing..." 
                        : `${uploadProgress}%`}
                    </span>
                  </div>
                  
                  <div className={`w-full h-2 rounded-full ${
                    isDarkMode ? "bg-white/10" : "bg-gray-200"
                  }`}>
                    <div 
                      className={`h-full rounded-full transition-all duration-300 ${
                        uploadProgress >= 95
                          ? isDarkMode ? "bg-green-400" : "bg-green-500"
                          : isDarkMode ? "bg-blue-400" : "bg-blue-500"
                      }`}
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                  
                  {uploadProgress >= 100 && (
                    <p className={`text-xs text-center italic ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}>
                      Finalizing and verifying your routine...
                    </p>
                  )}
                </div>
              )}
              
              {/* Success Message with Auto-Close Countdown */}
              {uploadState === "success" && (
                <div className={`p-4 rounded-lg ${
                  isDarkMode ? "bg-green-500/10" : "bg-green-50"
                }`}>
                  <div className="flex items-center">
                    <svg className={`h-5 w-5 mr-2 ${isDarkMode ? "text-green-400" : "text-green-500"}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className={isDarkMode ? "text-green-300" : "text-green-700"}>
                      The routine page will update automatically.
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoutineFormModal;
